/**
 * News Card Component
 *
 * Card component for single news item
 * Supports 'default' and 'hero' variants for different layout contexts
 */

import { Card, Badge, Text, Title, Button, Group, Stack, Box } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { NewsItem } from '../../../../data/news/newsTypes';
import { NewsCategoryBadge } from './NewsCategoryBadge';

interface NewsCardProps {
  item: NewsItem;
  featured?: boolean;
  showCTA?: boolean;
  /** Card variant: 'default' for grid items, 'hero' for full-width featured */
  variant?: 'default' | 'hero';
}

/**
 * News Card Component
 *
 * Displays a news item as a card with category, date, title, and excerpt.
 * Featured cards have gradient background and "Wyróżnione" badge.
 *
 * Variants:
 * - 'default': Standard card for grid layout
 * - 'hero': Full-width horizontal card for featured section
 */
export function NewsCard({ item, featured = false, showCTA = true, variant = 'default' }: NewsCardProps) {
  const formattedDate = new Date(item.date).toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Hero variant: horizontal layout for full-width featured section
  if (variant === 'hero') {
    return (
      <Card
        shadow="md"
        padding="xl"
        radius="lg"
        withBorder
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-ecoGreen-0) 0%, var(--mantine-color-ecoGreen-1) 100%)',
          borderColor: 'var(--mantine-color-ecoGreen-3)',
        }}
      >
        <Group
          gap="xl"
          align="flex-start"
          wrap="nowrap"
          style={{
            flexDirection: 'row',
          }}
          visibleFrom="sm"
        >
          {/* Left side: content */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Stack gap="md">
              {/* Header: Category badge + featured badge */}
              <Group justify="flex-start" gap="md">
                <NewsCategoryBadge category={item.category} />
                <Badge color="ecoGreen" variant="filled" size="md">
                  Wyróżnione
                </Badge>
              </Group>

              <Text size="sm" c="dimmed">
                {formattedDate}
              </Text>

              {/* Title - larger for hero */}
              <Title order={2} size="h3" style={{ lineHeight: 1.3 }}>
                {item.title}
              </Title>

              {/* Excerpt - more lines for hero */}
              <Text size="md" c="dimmed" lineClamp={3}>
                {item.excerpt}
              </Text>
            </Stack>
          </Box>

          {/* Right side: CTA */}
          {showCTA && (
            <Box style={{ flexShrink: 0 }}>
              <Button
                component={Link}
                to={`/news/${item.slug}`}
                variant="filled"
                color="ecoGreen"
                size="lg"
                rightSection={<IconArrowRight size={18} />}
              >
                Czytaj więcej
              </Button>
            </Box>
          )}
        </Group>

        {/* Mobile: vertical layout */}
        <Stack gap="md" hiddenFrom="sm">
          {/* Header: Category badge + featured badge */}
          <Group justify="flex-start" gap="md">
            <NewsCategoryBadge category={item.category} />
            <Badge color="ecoGreen" variant="filled" size="md">
              Wyróżnione
            </Badge>
          </Group>

          <Text size="sm" c="dimmed">
            {formattedDate}
          </Text>

          {/* Title */}
          <Title order={2} size="h3" style={{ lineHeight: 1.3 }}>
            {item.title}
          </Title>

          {/* Excerpt */}
          <Text size="md" c="dimmed">
            {item.excerpt}
          </Text>

          {/* CTA Button */}
          {showCTA && (
            <Button
              component={Link}
              to={`/news/${item.slug}`}
              variant="filled"
              color="ecoGreen"
              size="lg"
              rightSection={<IconArrowRight size={18} />}
              fullWidth
            >
              Czytaj więcej
            </Button>
          )}
        </Stack>
      </Card>
    );
  }

  // Default variant: standard card for grid layout
  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform var(--ec-transition-normal, 200ms ease), box-shadow var(--ec-transition-normal, 200ms ease)',
        background: featured
          ? 'linear-gradient(135deg, var(--mantine-color-ecoGreen-0) 0%, var(--mantine-color-ecoGreen-1) 100%)'
          : undefined,
      }}
    >
      <Stack gap="md" style={{ flex: 1 }}>
        {/* Header: Category badge + date + featured badge */}
        <Group justify="space-between" align="center">
          <NewsCategoryBadge category={item.category} />
          {featured && (
            <Badge color="ecoGreen" variant="filled" size="sm">
              Wyróżnione
            </Badge>
          )}
        </Group>

        <Text size="sm" c="dimmed">
          {formattedDate}
        </Text>

        {/* Title */}
        <Title order={3} size="h4" style={{ lineHeight: 1.3 }}>
          {item.title}
        </Title>

        {/* Excerpt */}
        <Text size="sm" c="dimmed" style={{ flex: 1 }}>
          {item.excerpt}
        </Text>

        {/* CTA Button */}
        {showCTA && (
          <Button
            component={Link}
            to={`/news/${item.slug}`}
            variant={featured ? 'filled' : 'light'}
            color="ecoGreen"
            rightSection={<IconArrowRight size={16} />}
            fullWidth
            mt="auto"
          >
            Czytaj więcej
          </Button>
        )}
      </Stack>
    </Card>
  );
}
